import { Input, Typography, Grid, Button, Divider, Collapse } from 'antd';
import { useTranslation } from 'react-i18next';
import { Application } from '../shared/VolunteerApplicationServiceTypes';
import { ApplicantDetailsTable } from '../pages/functionary/ApplicantDetailsTable';
import Icon from './ui-components/Icon';
import GrayChevronDown from '../assets/images/gray-chevron-down.svg';
import LeftArrow from '../assets/images/arrow-left.svg';
import LeftArrowWhite from '../assets/images/arrow-left-white.svg';

const { Text } = Typography;
const { useBreakpoint } = Grid;
const { Panel } = Collapse;

interface AdditionalDetailsFormForFunctioanryProps {
	application?: Application;
	onCancelClick: () => void;
}

const SectionHeader = ({
	heading,
	onCancelClick,
	isMobile,
}: {
	heading: string;
	onCancelClick: () => void;
	isMobile: boolean;
}) => {
	const { t } = useTranslation();
	return (
		<div className="relative flex items-center justify-between mb-4">
			{isMobile ? (
				<div onClick={onCancelClick}>
					<Icon src={LeftArrow} width={20} height={20} />
				</div>
			) : (
				<Button
					type="primary"
					onClick={onCancelClick}
					icon={<Icon src={LeftArrowWhite} width={'w-4'} height={'h-4'} />}
					className="absolute left-0"
				>
					{t('back')}
				</Button>
			)}
			<Text className="flex-grow text-center font-sans text-lg font-bold">
				{heading}
			</Text>
		</div>
	);
};

const InputLabelAndValue = ({
	labelText,
	value,
	showTextArea = false,
}: {
	labelText: string;
	value?: string | number | null;
	showTextArea?: boolean;
}) => {
	return (
		<div className="flex flex-col">
			<div className="flex gap-2 items-center">
				<Text className="mb-1 flex-2 text-xs font-normal font-sans text-secondary-black px-1">
					{labelText}
				</Text>
			</div>
			{showTextArea ? (
				<Input.TextArea
					className="h-16 text-sm font-normal font-sans text-light-gray rounded-lg pr-8"
					rows={4}
					value={value || ''}
					disabled
				/>
			) : (
				<Input
					className="h-9 text-sm font-normal font-sans text-light-gray rounded-lg pr-8"
					value={value || ''}
					disabled
				/>
			)}
		</div>
	);
};

export const AdditionalDetailsFormForFunctioanry: React.FC<
	AdditionalDetailsFormForFunctioanryProps
> = ({
	application,
	onCancelClick,
}: AdditionalDetailsFormForFunctioanryProps) => {
	const { t } = useTranslation();
	const screens = useBreakpoint();
	const isMobile = !screens.sm;

	const formatLanguage = (str?: string) =>
		str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : undefined;

	const courseAvailability = application?.availabilityForTrainingCourse;

	const familyMemberRole = application?.familyMemberReference?.isAbhyasi
		? t('anAbhyasi')
		: application?.familyMemberReference?.isPreceptor
		? t('aPreceptor')
		: t('notYetAnAbhyasi');

	const isOtherRefProvided = application?.otherReferenceProvided;
	const otherRefRole = application?.otherReference?.isAbhyasi
		? t('anAbhyasi')
		: application?.otherReference?.isPreceptor
		? t('aPreceptor')
		: t('notYetAnAbhyasi');

	const CandidateDetails = (
		<div className="flex flex-col mb-8">
			{!isMobile && (
				<Divider orientation="left" orientationMargin="0">
					<div className="text-base font-bold font-sans text-primary-blue">
						{t('candidateDetails')}
					</div>
				</Divider>
			)}
			<div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
				<InputLabelAndValue
					labelText={t('selectedCurrentCenter')}
					value={application?.center?.name}
				/>
				<InputLabelAndValue
					labelText={t('monthOfFirstSitting')}
					value={application?.applicant?.dateOfJoining}
				/>
				<InputLabelAndValue
					labelText={t('dateOfBirth')}
					value={application?.applicant?.dateOfBirth}
				/>
			</div>
		</div>
	);

	const EducationAndJobDetails = (
		<div className="flex flex-col mb-8">
			{!isMobile && (
				<Divider orientation="left" orientationMargin="0">
					<div className="text-base font-bold font-sans text-primary-blue">
						{t('educationAndJobDetails')}
					</div>
				</Divider>
			)}
			<div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
				<InputLabelAndValue
					labelText={t('educationQualification')}
					value={application?.applicant?.educationQualification}
				/>
				<InputLabelAndValue
					labelText={t('profession')}
					value={application?.applicant?.profession}
				/>
			</div>
			<div className="grid grid-cols-1 gap-4 mt-4">
				<InputLabelAndValue
					labelText={t('professionDetails')}
					value={application?.applicant?.professionDetails}
					showTextArea
				/>
			</div>
		</div>
	);

	const AddressDetails = (
		<div className="flex flex-col mb-8">
			{!isMobile && (
				<Divider orientation="left" orientationMargin="0">
					<div className="text-base font-bold font-sans text-primary-blue">
						{t('addressDetails')}
					</div>
				</Divider>
			)}
			<div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
				<InputLabelAndValue
					labelText={t('addressLine1')}
					value={application?.applicant?.addressLine1}
				/>
				<InputLabelAndValue
					labelText={t('addressLine2')}
					value={application?.applicant?.addressLine2}
				/>
				<InputLabelAndValue
					labelText={t('addressLine3')}
					value={application?.applicant?.addressLine3}
				/>
				<InputLabelAndValue
					labelText={t('city')}
					value={application?.applicant?.city}
				/>
				<InputLabelAndValue
					labelText={t('pincode')}
					value={application?.applicant?.pincode}
				/>
			</div>
		</div>
	);

	const TrainingCourseDetails = (
		<div className="flex flex-col mb-8">
			{!isMobile && (
				<Divider orientation="left" orientationMargin="0">
					<div className="text-base font-bold font-sans text-primary-blue">
						{t('trainingCourseDetails')}
					</div>
				</Divider>
			)}
			<div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
				{courseAvailability && (
					<InputLabelAndValue
						labelText={t('availabilityForTheCourse')}
						value={t(courseAvailability)}
					/>
				)}
				<InputLabelAndValue
					labelText={t('preferredTrainingLanguage')}
					value={formatLanguage(application?.preferredLanguageOfTraining)}
				/>
			</div>
		</div>
	);

	const PreceptorContactDetails = (
		<div className="flex flex-col mb-8">
			{!isMobile && (
				<Divider orientation="left" orientationMargin="0">
					<div className="text-base font-bold font-sans text-primary-blue">
						{t('preceptorContact')}
					</div>
				</Divider>
			)}
			<div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
				<InputLabelAndValue
					labelText={t('preceptorName')}
					value={application?.preceptorReference?.name}
				/>
				<InputLabelAndValue
					labelText={t('preceptorMobileNo')}
					value={application?.preceptorReference?.mobile}
				/>
			</div>
		</div>
	);

	const FamilyMemberContactDetails = (
		<div className="flex flex-col mb-8">
			{!isMobile && (
				<Divider orientation="left" orientationMargin="0">
					<div className="text-base font-bold font-sans text-primary-blue">
						{t('familyMemberContact')}
					</div>
				</Divider>
			)}
			<div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
				<InputLabelAndValue
					labelText={t('familyMemberName')}
					value={application?.familyMemberReference?.name}
				/>
				<InputLabelAndValue
					labelText={t('familyMemberMobileNo')}
					value={application?.familyMemberReference?.mobile}
				/>
				<InputLabelAndValue
					labelText={t('myRelationshipWithFamilyMember')}
					value={formatLanguage(
						application?.familyMemberReference?.relationship
					)}
				/>
				<InputLabelAndValue
					labelText={t('isTheFamilyMember')}
					value={familyMemberRole}
				/>
			</div>
		</div>
	);

	const OtherReferenceDetails = (
		<div className="flex flex-col mb-8">
			{!isMobile && (
				<Divider orientation="left" orientationMargin="0">
					<div className="text-base font-bold font-sans text-primary-blue">
						{t('otherReference')}
					</div>
				</Divider>
			)}
			<div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
				<InputLabelAndValue
					labelText={t('otherReferenceName')}
					value={application?.otherReference?.name}
				/>
				<InputLabelAndValue
					labelText={t('otherReferenceMobile')}
					value={application?.otherReference?.mobile}
				/>
				<InputLabelAndValue labelText={t('isThePerson')} value={otherRefRole} />
			</div>
		</div>
	);

	const Header = ({ title }: { title: string }) => (
		<Text className="font-poppins font-semibold text-xs text-gray-650">
			{t(title)}
		</Text>
	);

	return (
		<div className="flex justify-center">
			<div
				className={`flex flex-col h-full min-h-0 p-[8px] ${
					isMobile ? 'w-full' : 'w-[90%]'
				}`}
			>
				<SectionHeader
					heading={t('additionalDetails')}
					onCancelClick={onCancelClick}
					isMobile={isMobile}
				/>
				<ApplicantDetailsTable applicantInfo={application?.applicant} />
				<div className={`flex justify-center ${isMobile ? 'mt-0' : 'mt-8'}`}>
					<div
						className={`bg-white rounded-lg ${
							isMobile
								? 'w-full py-4'
								: 'w-[95%] px-52 py-12 border border-solid border-[#E6E6E6]'
						}`}
					>
						{isMobile ? (
							<>
								<Collapse
									ghost
									expandIconPosition="end"
									expandIcon={({ isActive }) => (
										<span
											className={`transition-transform ${
												isActive ? 'rotate-180' : 'rotate-0'
											}`}
										>
											<Icon src={GrayChevronDown} width={18} height={18} />
										</span>
									)}
								>
									<Panel
										className="mb-4 !border !border-solid !border-[#E6E6E6] !rounded"
										header={<Header title={'additionalDetails'} />}
										key="1"
									>
										{CandidateDetails}
										{EducationAndJobDetails}
										{AddressDetails}
									</Panel>
									<Panel
										className="mb-4 !border !border-solid !border-[#E6E6E6] !rounded"
										header={<Header title={'trainingCourseDetails'} />}
										key="2"
									>
										{TrainingCourseDetails}
									</Panel>
									<Panel
										className="mb-4 !border !border-solid !border-[#E6E6E6] !rounded"
										header={<Header title={'preceptorContact'} />}
										key="3"
									>
										{PreceptorContactDetails}
									</Panel>
									<Panel
										className="mb-4 !border !border-solid !border-[#E6E6E6] !rounded"
										header={<Header title={'familyMemberContact'} />}
										key="4"
									>
										{FamilyMemberContactDetails}
									</Panel>
									{isOtherRefProvided && (
										<Panel
											className="mb-4 !border !border-solid !border-[#E6E6E6] !rounded"
											header={<Header title={'otherReference'} />}
											key="5"
										>
											{OtherReferenceDetails}
										</Panel>
									)}
								</Collapse>
							</>
						) : (
							<>
								{CandidateDetails}
								{EducationAndJobDetails}
								{AddressDetails}
								{TrainingCourseDetails}
								{PreceptorContactDetails}
								{FamilyMemberContactDetails}
								{isOtherRefProvided && <>{OtherReferenceDetails}</>}
							</>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};
